import React, { useMemo } from 'react'
import { useFormContext, Controller, useFieldArray } from 'react-hook-form'
import { Form } from 'react-bootstrap'
import { ProductVariantSelector } from 'components/UI/ProductVariantSelector'
import type { ProductWithVariants } from 'components/UI/ProductVariantSelector'
import apiService from 'services/api'
import {
  mapVariantToDisplay,
  findLineItemIndicesForProduct,
  findLineItemIndexForVariant,
} from '../../../utils/productHelpers'

export function ProductSelectionStep() {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'line_items',
  })

  const api = apiService()
  const { data: products, isLoading: isLoadingProducts } = api.useGetProducts()

  // Convert line items to ProductWithVariants format for the selector
  // Group line items by product so they display together
  const selectionsForDisplay = useMemo((): ProductWithVariants[] => {
    // Group line items by product
    const productMap = new Map<number, ProductWithVariants>()

    fields.forEach((field: any, index) => {
      const variantId = watch(`line_items.${index}.variant_id`)

      if (variantId && products) {
        for (const product of products) {
          const variant = product.variants?.find((v) => v.id === variantId)
          if (variant) {
            const existing = productMap.get(product.id)
            if (existing) {
              // Add variant to existing product
              existing.variantIds.push(variant.id || 0)
              existing.variants.push(mapVariantToDisplay(variant))
            } else {
              // Create new product entry
              productMap.set(product.id, {
                productId: product.id,
                productName: product.name,
                variantIds: [variant.id || 0],
                variants: [mapVariantToDisplay(variant)],
              })
            }
            break
          }
        }
      }
    })

    return Array.from(productMap.values())
  }, [fields, products, watch])

  // Handle adding new variants (creates line items for each variant)
  const handleAddVariant = (selection: ProductWithVariants) => {
    // Create a separate line item for each selected variant
    selection.variantIds.forEach((variantId) => {
      append({
        variant_id: variantId,
        quantity: 1,
      })
    })
  }

  // Handle editing variants (replaces existing line items with new selection)
  const handleEditVariant = (index: number, selection: ProductWithVariants) => {
    // Get the current product's line items
    const currentProductId = selection.productId

    // Find all line items for this product
    const productLineItemIndices = findLineItemIndicesForProduct(
      fields,
      currentProductId,
      watch,
      products
    )

    // Store existing quantity for each variant
    const existingLineItemData = new Map<number, { quantity: number }>()

    productLineItemIndices.forEach((idx) => {
      const variantId = watch(`line_items.${idx}.variant_id`)
      if (variantId) {
        existingLineItemData.set(variantId, {
          quantity: watch(`line_items.${idx}.quantity`) || 1,
        })
      }
    })

    // Remove all existing line items for this product (in reverse to maintain indices)
    productLineItemIndices.reverse().forEach((idx) => {
      remove(idx)
    })

    // Add new line items for each selected variant
    // Preserve existing quantity if variant was already selected
    selection.variantIds.forEach((variantId) => {
      const existingData = existingLineItemData.get(variantId)
      append({
        variant_id: variantId,
        quantity: existingData?.quantity || 1,
      })
    })
  }

  // Handle removing a product (removes all line items for that product)
  const handleRemoveVariant = (selectionIndex: number) => {
    // Get the product from selections
    const selection = selectionsForDisplay[selectionIndex]
    if (!selection) return

    // Find all line items for this product and remove them (in reverse to maintain indices)
    const lineItemIndicesToRemove: number[] = []
    fields.forEach((field: any, idx) => {
      const variantId = watch(`line_items.${idx}.variant_id`)
      if (selection.variantIds.includes(variantId)) {
        lineItemIndicesToRemove.push(idx)
      }
    })

    // Remove in reverse order to maintain indices
    lineItemIndicesToRemove.reverse().forEach((idx) => {
      remove(idx)
    })
  }

  return (
    <div>
      <h5 className="text-lg font-medium mb-1">Products</h5>
      <p className="text-sm text-gray-500 mb-4">
        You can add multiple products to this request. Select a product and
        choose its variants.
      </p>

      {/* Use ProductVariantSelector with custom rendering for quantity/format */}
      <ProductVariantSelector
        products={products}
        isLoadingProducts={isLoadingProducts}
        selections={selectionsForDisplay}
        onAdd={handleAddVariant}
        onEdit={handleEditVariant}
        onRemove={handleRemoveVariant}
        multipleProducts={true}
        multipleVariants={true}
        showVariantList={true}
        selectorLabel="Select Product"
        selectButtonText="Add Product Variants"
        emptyStateText="No product variants added yet. Select a product above and choose variants to get started."
        renderAdditionalContent={(selection) => {
          // Render quantity and format fields for each variant in this product
          return (
            <div className="mt-3 pt-3 border-t border-gray-200 space-y-4">
              {selection.variants.map((variant) => {
                // Find the line item index for this variant
                const lineItemIndex = findLineItemIndexForVariant(
                  fields,
                  variant.id,
                  watch
                )

                if (lineItemIndex === -1) return null

                return (
                  <div key={variant.id}>
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      {variant.name}
                      {variant.format_pack_size && (
                        <span className="ml-2 text-gray-500">
                          ({variant.format_pack_size})
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Form.Label className="font-semibold mb-2">
                          Quantity *
                        </Form.Label>
                        <Controller
                          name={`line_items.${lineItemIndex}.quantity`}
                          control={control}
                          render={({ field }) => (
                            <>
                              <Form.Control
                                type="number"
                                {...field}
                                onChange={(e) =>
                                  field.onChange(Number(e.target.value))
                                }
                                min={1}
                                isInvalid={
                                  !!(errors.line_items as any)?.[lineItemIndex]
                                    ?.quantity
                                }
                                placeholder="Qty"
                              />
                              {(errors.line_items as any)?.[lineItemIndex]
                                ?.quantity && (
                                <div className="text-red-600 text-sm mt-1">
                                  {
                                    (errors.line_items as any)[lineItemIndex]
                                      .quantity.message as string
                                  }
                                </div>
                              )}
                            </>
                          )}
                        />
                      </div>

                      <div>
                        <Form.Label className="font-semibold mb-2">
                          Format
                        </Form.Label>
                        <div className="form-control-plaintext text-gray-700">
                          {variant.format_pack_size || '—'}
                        </div>
                      </div>
                    </div>

                    {/* Hidden field for variant_id */}
                    <Controller
                      name={`line_items.${lineItemIndex}.variant_id`}
                      control={control}
                      render={({ field }) => (
                        <input type="hidden" tabIndex={-1} {...field} />
                      )}
                    />
                  </div>
                )
              })}
            </div>
          )
        }}
      />

      {errors.line_items && typeof errors.line_items.message === 'string' && (
        <div className="text-red-600 text-sm mt-1">
          {errors.line_items.message}
        </div>
      )}
    </div>
  )
}
